import { COMEDIAN_TAG_PROVIDER_REGISTRY } from "./registry";

export type ComedianGoogleTagDestination =
  | { kind: "ga4"; id: string }
  | {
      kind: "google_ads";
      id: string;
      conversion_labels?: { purchase?: string };
    };

export type ComedianGoogleTagConfig = {
  destinations: ComedianGoogleTagDestination[];
};

function parsePurchaseLabel(
  raw: unknown,
  pattern: RegExp
): { purchase?: string } | undefined {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return undefined;
  }
  const purchase = (raw as Record<string, unknown>).purchase;
  return typeof purchase === "string" && pattern.test(purchase)
    ? { purchase }
    : undefined;
}

/**
 * Tolerant parse of a `google_tag` row's `config` jsonb down to its
 * `destinations` array. The column is untrusted input, so any shape that
 * isn't a plain `{ destinations: [...] }` object resolves to `[]`, and each
 * entry is checked against the registry's destination `idPattern` (and, for
 * `google_ads`, `conversionLabelPattern`) individually — one malformed entry
 * drops only itself, never its siblings. Never throws.
 */
export function getGoogleTagDestinations(
  config: unknown
): ComedianGoogleTagDestination[] {
  if (typeof config !== "object" || config === null || Array.isArray(config)) {
    return [];
  }

  const destinations = (config as Record<string, unknown>).destinations;
  if (!Array.isArray(destinations)) {
    return [];
  }

  const specs = COMEDIAN_TAG_PROVIDER_REGISTRY.google_tag.destinations;
  if (!specs) {
    return [];
  }

  const parsed: ComedianGoogleTagDestination[] = [];
  for (const entry of destinations) {
    if (typeof entry !== "object" || entry === null || Array.isArray(entry)) {
      continue;
    }
    const record = entry as Record<string, unknown>;
    const id = record.id;

    if (
      record.kind === "ga4" &&
      typeof id === "string" &&
      specs.ga4.idPattern.test(id)
    ) {
      parsed.push({ kind: "ga4", id });
      continue;
    }

    if (
      record.kind === "google_ads" &&
      typeof id === "string" &&
      specs.google_ads.idPattern.test(id)
    ) {
      const conversionLabels = specs.google_ads.conversionLabelPattern
        ? parsePurchaseLabel(
            record.conversion_labels,
            specs.google_ads.conversionLabelPattern
          )
        : undefined;
      parsed.push({
        kind: "google_ads",
        id,
        ...(conversionLabels ? { conversion_labels: conversionLabels } : {}),
      });
    }
  }

  return parsed;
}
